<template>
	<div class="history-sidebar">
		<button class="new-topic-btn" @click="handleNewTopic">开启新话题</button>
		<div class="history-list" ref="historyList" @scroll="handleScroll">
			<div v-for="(group, groupIndex) in groupedHistory" :key="groupIndex" class="history-group">
				<div class="time-label">{{ group.label }}</div>
				<div
					v-for="(item, index) in group.items"
					:key="item.id || index"
					class="history-item"
					:class="{ active: String(item.id) === String(currentConversationId) }"
					@click="selectChat(item.id)"
				>
					{{ item.title }}
				</div>
			</div>
			<div v-if="loading" class="loading-more">加载中...</div>
			<div v-if="!hasMore && history.length > 0" class="no-more">没有更多了</div>
		</div>
	</div>
</template>

<script>
import { DateFormatter } from '@/utils/filter';

export default {
	name: 'HistorySidebar',
	props: {
		currentConversationId: {
			type: [Number, String],
			default: null
		}
	},
	data() {
		return {
			history: [],
			historyPage: 1,
			historyPageSize: 30,
			loading: false,
			hasMore: true,
			refreshTimer: null // 防抖定时器
		};
	},
	mounted() {
		this.fetchHistory();
	},
	computed: {
		// 按时间分组历史记录
		groupedHistory() {
			const groups = {
				today: { label: '今天', items: [] },
				yesterday: { label: '昨天', items: [] },
				week: { label: '7天内', items: [] },
				month: { label: '30天内', items: [] },
				older: { label: '30天以上', items: [] }
			};

			this.history.forEach(item => {
				// 使用 created_at 字段
				const itemTime = item.created_at ? new Date(item.created_at) : (item.time ? new Date(item.time) : null);
				if (!itemTime || isNaN(itemTime.getTime())) return;
				
				const relativeTime = DateFormatter.formatRelativeTime(itemTime);
				if (relativeTime === '今天') {
					groups.today.items.push(item);
				} else if (relativeTime === '昨天') {
					groups.yesterday.items.push(item);
				} else if (relativeTime === '7天内') {
					groups.week.items.push(item);
				} else if (relativeTime === '30天内') {
					groups.month.items.push(item);
				} else {
					groups.older.items.push(item);
				}
			});

			return Object.values(groups).filter(group => group.items.length > 0);
		}
	},
	methods: {
		selectChat(chatId) {
			const chat = this.history.find(item => item.id === chatId);
			if (chat) {
				// 传递完整的历史记录对象给父组件
				this.$emit('select-chat', chat);
			}
		},
		handleNewTopic() {
			this.$emit('new-topic');
		},
		handleScroll() {
			const el = this.$refs.historyList;
			if (!el) return;
			
			// 滚动到底部时加载更多
			if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
				this.loadMoreHistory();
			}
		},
		// 获取历史记录
		async fetchHistory(reset = false) {
			if (this.loading) return;
			
			if (reset) {
				this.historyPage = 1;
				this.history = [];
				this.hasMore = true;
			}
			
			this.loading = true;
			try {
				const res = await this.$get('/manager-conversation', {
					page: this.historyPage,
					page_size: this.historyPageSize
				});
				
				if (res.code === 200 && res.data) {
					if (reset) {
						this.history = res.data;
					} else {
						// 合并数据并去重（基于 id）
						const existingIds = new Set(this.history.map(item => String(item.id)));
						const newItems = res.data.filter(item => !existingIds.has(String(item.id)));
						this.history = [...this.history, ...newItems];
					}
					
					// 判断是否还有更多数据
					this.hasMore = this.history.length < res.total;
					if (this.hasMore) {
						this.historyPage++;
					}
				}
			} catch (error) {
				console.error('获取历史记录失败:', error);
			} finally {
				this.loading = false;
			}
		},
		// 加载更多历史记录
		loadMoreHistory() {
			if (!this.loading && this.hasMore) {
				this.fetchHistory();
			}
		},
		// 刷新历史记录（重置并重新获取第一页）
		async refreshHistory() {
			// 清除之前的定时器，实现防抖
			if (this.refreshTimer) {
				clearTimeout(this.refreshTimer);
			}
			
			// 设置防抖，300ms 内多次调用只执行最后一次
			this.refreshTimer = setTimeout(async () => {
				await this.fetchHistory(true);
				// 刷新后，由于 currentConversationId prop 已更新，会自动高亮选中
				// 确保新话题在视图中可见（滚动到顶部，因为新话题通常在列表最前面）
				this.$nextTick(() => {
					const listEl = this.$refs.historyList;
					if (listEl) {
						listEl.scrollTop = 0;
					}
				});
				this.refreshTimer = null;
			}, 300);
		}
	}
};
</script>

<style lang="scss" scoped>
.history-sidebar {
	width: 300px;
	height: calc(100vh - 100px);
	padding: 10px;
	border-radius: 10px;
	box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.4);
	background: #fff;
	display: flex;
	flex-direction: column;
	// /* overflow: hidden; */
	position: fixed;
	left: 30px;
	top: 70px;
	z-index: 9;
}

.new-topic-btn {
	width: 100%;
	margin: 0 auto;
	padding: 10px 10px;
	border-radius: 24px;
	background: #fff;
	border: none;
	font-size: 13px;
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.5);
	cursor: pointer;
	transition: all 0.3s ease;
}

.history-list {
	flex: 1;
	margin-top: 15px;
	overflow-y: auto;
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.history-group {
	margin-bottom: 20px;
}

.time-label {
	color: #999;
	margin-bottom: 5px;
	padding: 0 4px;
}

.history-item {
	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin-bottom: 8px;
	padding: 4px 6px;
}

.history-item.active {
	background: #f5f5f5;
	border-radius: 18px;
}

.loading-more,
.no-more {
	text-align: center;
	padding: 10px;
	color: #999;
	font-size: 12px;
}
</style>

